/*
  State model for synth parent test results
  Base class for subtest and preview test results state models
*/
import { action, computed, observable } from 'mobx';
import { uniqueId, uniqBy } from 'lodash';
import moment from 'moment';
import { MAX_TRACE_ITEMS } from '@kentik/ui-shared/synthetics/constants';
import {
  getHealthTimeline,
  getTimelineAlarmsByResultTimeMs,
  makeAlertManagerAlarm
} from '@kentik/ui-shared/synthetics/utils';
import { getTraceroutes } from '@kentik/ui-shared/synthetics/tracerouteUtils';
import {
  DEFAULT_LOOKBACK,
  DEFAULT_MESH_LOOKBACK,
  DEFAULT_MIN_LOOKBACK,
  TEST_TYPES,
  SYNGEST_CUTOVER
} from 'app/util/constants';
import { parseQueryString } from 'app/util/utils';
import {
  getTimelinebounds,
  setLookbackFromHistory,
  getMinLookback,
  getAggregationLabel,
  getRawHealthTimeRange
} from 'app/views/synthetics/utils/syntheticsUtils';
import { getStartAndEndFromLookback } from 'core/util/dateUtils';
import { isIpValid } from 'core/util/ip';
import { processTestResults, getMesh, getAgentIdsForTraceResults } from './utils';

class TestResultsState {
  get defaults() {
    return {
      init: false,
      initializedWithResults: false,
      // state data
      test: null,
      startDate: null,
      endDate: null,
      actualStartDate: null,
      actualEndDate: null,
      lookbackSeconds: DEFAULT_LOOKBACK,
      minLookbackSeconds: DEFAULT_MIN_LOOKBACK,
      // loading indicators
      loadingAlarmResults: false,
      loadingResults: false,
      loadingTimestamps: false,
      loadingTraceResults: false,
      // results indicators
      hasAlarmResults: false,
      // returned results
      alarmResults: null,
      resultsTs: {},
      timestamps: [],
      traceResultsTs: {},
      // supports selection state
      aggregationData: {},
      lastFetchedTraceParams: {
        agentIds: [],
        targetIps: [],
        allTargets: [],
        selectedTargets: []
      },
      pageTabId: 'agents',
      resultTime: null,
      exportTime: null,
      // display/feature state
      displayOptions: {
        allowAgentDetails: false,
        allowBgp: false,
        allowExport: true,
        allowHealthTimeline: false,
        allowMap: false,
        allowMesh: false,
        allowPath: false,
        allowSankey: false,
        allowSettings: true,
        allowShare: true,
        allowSummary: true,
        hideHealthAggAgentSelector: false,
        hideTitle: false,
        shared: false,
        usePageComponent: true
      }
    };
  }

  requestId = uniqueId('tr_');

  // indicates state has been initialized, beginning with the first fetch for timelines
  @observable
  init = this.defaults.init;

  // indicates syngest results have been loaded manually, skipping the usual fetch cycle
  // this is currently most commonly done for ai advisor test results
  @observable
  initializedWithResults = this.defaults.initializedWithResults;

  // linked test model
  test = this.defaults.test;

  // submitted start data that can be used to hydrate actualStartState
  @observable
  startDate = this.defaults.startDate;

  // submitted end data that can be used to hydrate actualEndState
  @observable
  endDate = this.defaults.endDate;

  // hydrated from lookbackSeconds/startDate, used for fetch
  @observable
  actualStartDate = this.defaults.actualStartDate;

  // hydrated from lookbackSeconds/endDate, used for fetch
  @observable
  actualEndDate = this.defaults.actualEndDate;

  // determines actual start/end date when present
  @observable
  lookbackSeconds = this.defaults.lookbackSeconds;

  // used in date picking to determine the minimum lookback allowed for the given test
  @observable
  minLookbackSeconds = this.defaults.minLookbackSeconds;

  @observable
  loadingAlarmResults = this.defaults.loadingAlarmResults;

  @observable
  loadingResults = this.defaults.loadingResults;

  @observable
  loadingTimestamps = this.defaults.loadingTimestamps;

  @observable
  loadingTraceResults = this.defaults.loadingTraceResults;

  @observable
  hasAlarmResults = this.defaults.hasAlarmResults;

  // results returned from an alarm fetch request
  @observable.ref
  alarmResults = this.defaults.alarmResults;

  // a hash keyed by timestamp mapped to corresponding test results
  // this map fills in over time when clicking items in the alarm timeline for example
  resultsTs = this.defaults.resultsTs;

  // results returned from a timestamp fetch request
  // this will be a list of valid timestamps that can be used to fetch test data
  @observable.ref
  timestamps = this.defaults.timestamps;

  // returned from syngest results call
  // includes maxRawLookback, isAggregated, and results properties
  // 'results' will contain avg metrics calculated in syngest and displayed in the ui
  @observable.ref
  aggregationData = this.defaults.aggregationData;

  // a hash keyed by timestamp mapped to corresponding trace results
  // this map fills in over time when clicking items in the alarm timeline for example
  traceResultsTs = this.defaults.traceResultsTs;

  @observable.ref
  lastFetchedTraceParams = this.defaults.lastFetchedTraceParams;

  // determines the selected data tab in the parent test results ui
  @observable
  pageTabId = this.defaults.pageTabId;

  // the currently selected timestamp in seconds
  @observable
  resultTime = this.defaults.resultTime;

  // the selected timestamp at time of export
  exportTime = this.defaults.exportTime;

  // manages a number of settings used for controlling feature state
  // for example allowPath=true means we will show the path panel
  displayOptions = this.defaults.displayOptions;

  // captures history from react router, primarily used for overriding date selection via querystring
  history = null;

  // used in the mesh ui component
  meshState = {};

  constructor({ store, test }) {
    this.store = store;
    this.test = test;
  }

  newRequestID() {
    this.requestId = uniqueId('tr_');
  }

  configure({ startDate, endDate, lookbackSeconds, history, force = false, ...displayOptions } = {}) {
    const { test } = this;
    const { hasPing, hasBGPMonitoring } = test;
    const isDns = test.get('test_type') === TEST_TYPES.DNS;

    // capture the export timestamp if passed in
    this.updateExportTime(history);

    // mesh is the only test type where we want to override the default page tab when loading from cache
    this.initMeshPageTab();

    if (!this.init) {
      Object.entries(displayOptions).forEach(([key, value]) => {
        if (this.displayOptions[key] !== undefined) {
          this.displayOptions[key] = value;
        }
      });

      const { usePageComponent, shared } = this.displayOptions;

      this.displayOptions.allowMesh = !isDns;
      this.displayOptions.allowSankey = !isDns;
      this.displayOptions.allowMap = !isDns || !this.store.$app.isExport;
      this.displayOptions.allowPath = hasPing;
      this.displayOptions.allowHealthTimeline = true;
      this.displayOptions.allowBgp = hasBGPMonitoring && usePageComponent;
      this.displayOptions.allowAgentDetails = !shared;
      this.history = history;

      this.initiate({ startDate, endDate, lookbackSeconds });
    } else if (
      force ||
      (startDate && this.startDate !== startDate) ||
      (endDate && this.endDate !== endDate) ||
      (lookbackSeconds && this.lookbackSeconds !== lookbackSeconds)
    ) {
      this.onDateRangeChange({ startDate, endDate, lookbackSeconds });
    }
  }

  initiate({ startDate, endDate, lookbackSeconds }) {
    const { history, test } = this;
    const { $exports } = this.store;
    const { allowSettings } = this.displayOptions;
    const hasLocationHistory =
      this.checkForDateRange(history?.location?.state || {}) || parseQueryString(history?.location?.search).start;
    const hasBeenProvidedADateRange = this.checkForDateRange({ startDate, endDate, lookbackSeconds });
    const defaultLookbackSeconds = this.test.isMesh ? DEFAULT_MESH_LOOKBACK : this.lookbackSeconds;
    let initPromise = Promise.resolve({
      startDate: this.startDate,
      endDate: this.endDate,
      lookbackSeconds: defaultLookbackSeconds
    });

    // initialize the page tab by test type, allowing for override below based on hash settings
    this.initPageTab();

    // we support too many ways to configure date range
    if (hasBeenProvidedADateRange) {
      initPromise = Promise.resolve({ startDate, endDate, lookbackSeconds });
    } else if (hasLocationHistory && allowSettings) {
      initPromise = setLookbackFromHistory({ $exports, history, test }).then(() => null);
    }

    return initPromise
      .then((dateObj) => {
        if (allowSettings) {
          return $exports
            .getSettings()
            .then(({ hashedPageTabId, hashedLookbackSeconds, hashedStartDate, hashedEndDate }) => {
              if (hashedPageTabId !== undefined) {
                this.onPageTabChange(hashedPageTabId);
              }

              const hasDateRangeSettings = this.checkForDateRange({
                startDate: hashedStartDate,
                endDate: hashedEndDate,
                lookbackSeconds: hashedLookbackSeconds
              });

              if (hasDateRangeSettings && !hasBeenProvidedADateRange) {
                return {
                  startDate: hashedStartDate,
                  endDate: hashedEndDate,
                  lookbackSeconds: hashedLookbackSeconds
                };
              }
              return dateObj;
            });
        }
        return dateObj;
      })
      .then((dateObj) => {
        this.onDateRangeChange(dateObj);
      });
  }

  initPageTab() {
    const { test } = this;

    if (test.isMesh) {
      this.initMeshPageTab();
    } else if (test.isFlowBased) {
      this.pageTabId = 'sankey';
    }
  }

  // provides an override to page tab initialization for mesh tests
  // when showing aggregate data, mesh tests will load with the 'agents' tab selected
  initMeshPageTab() {
    const { test } = this;

    if (test.isMesh) {
      this.pageTabId = this.canShowRawHealth ? 'agents' : 'mesh';
    }
  }

  // ----- fetchers ------------------------------------------------

  // fetches fresh alarms, test results, and trace results when the selected date changes
  fetch({ startDate, endDate }) {
    this.newRequestID();

    // prevent flashes of empty state when executing fresh fetches
    // cache is reset on these fresh fetches so we're sure to have loading state managed correctly moving forward
    this.loadingTraceResults = true;

    this.fetchResultsTimelines({ start_time: startDate, end_time: endDate }).then(() =>
      this.fetchAlarmResults({
        start: moment.utc(startDate * 1000).toISOString(),
        end: moment.utc(endDate * 1000).toISOString()
      }).then(() => {
        // reset state for fresh date range
        this.lastFetchedTraceParams = this.defaults.lastFetchedTraceParams;
        this.traceResultsTs = this.defaults.traceResultsTs;

        // when loading a fresh time range, we will attempt to load trace results giving alarmed agent ids and target ips preference
        const { agentIds: alarmedAgentIds, targetIps: alarmedTargetIps } = this.alarmedAgentIdsAndTargetIps;
        const agentIds = getAgentIdsForTraceResults(this.test, alarmedAgentIds, MAX_TRACE_ITEMS);

        this.fetchTraceResults({
          agent_ids: agentIds,
          target_ips: alarmedTargetIps.slice(0, MAX_TRACE_ITEMS)
        });
      })
    );
  }

  // fetches a list of valid timestamps for the given test and selected date range
  // if there are timestamps, the most recent is used to fetch data for that time slice
  @action
  fetchResultsTimelines(dateObj) {
    const { $syn } = this.store;
    const { requestId, test } = this;
    const { isPreset } = test;

    this.loadingTimestamps = true;
    this.timestamps = this.defaults.timestamps;
    this.resultsTs = this.defaults.resultsTs;
    this.resultTime = this.defaults.resultTime;
    this.aggregationData = this.defaults.aggregationData;

    if (!this.init) {
      this.init = true;
    }

    return $syn.requests.fetchTestResultsTimestamps({ ...dateObj, test_id: `${test.id}` }, { preset: isPreset }).then(
      action(({ timestamps, aggregation }) => {
        // check if request is relevant before storing response
        if (requestId === this.requestId) {
          const hasTimestamps = !!timestamps.length;
          let ts = hasTimestamps ? timestamps[timestamps.length - 1] : null;

          if (this.exportTime) {
            // override the selected timestamp when exporting
            ts = this.exportTime;
          }

          this.timestamps = hasTimestamps ? timestamps : [];
          this.resultTime = +ts;
          this.loadingTimestamps = false;

          if (hasTimestamps) {
            this.fetchResultsByTs(ts, aggregation);
          }
        }
      })
    );
  }

  // fetches test results for a given timestamp
  // this will happen on load and when the selected date changes
  @action
  fetchResultsByTs(ts, aggregation) {
    const { $syn } = this.store;
    const { requestId, test } = this;
    const { isPreset } = test;

    if (!this.resultsTs[ts]) {
      this.loadingResults = true;

      return $syn.requests
        .fetchTestResultsByTimestamp({ timestamp: ts, aggregation, test_id: `${test.id}` }, { preset: isPreset })
        .then(
          action((resp) => {
            // check if request is relevant before storing response
            if (requestId === this.requestId) {
              this.resultsTs[ts] = resp?.tests_health || null;

              // capture the max raw lookback and aggregation state of the syngest results
              this.aggregationData = resp?.tests_health?.book?.aggregationData || {};

              // add the aggregation returned from the timestamps call to aggregationData
              // this will now match what comes back from subtest result responses
              this.aggregationData.aggregation = aggregation;

              if (this.resultTime === +ts) {
                this.loadingResults = false;
              }
            }
          })
        );
    }

    return Promise.resolve();
  }

  // fetches alarm results for the given test and selected date range
  @action
  fetchAlarmResults(dateObj) {
    const { $syn } = this.store;
    const { requestId, test } = this;

    // reset state
    this.alarmResults = this.defaults.alarmResults;
    this.hasAlarmResults = this.defaults.hasAlarmResults;
    this.loadingAlarmResults = true;
    return $syn.requests.getTestAlarms({ ...dateObj, ids: [test.id] }, { preset: test.isPreset }).then(
      action((resp) => {
        // check if request is relevant before storing response
        if (requestId === this.requestId) {
          this.alarmResults = {
            ...resp,
            alarms: resp.alarms.map(makeAlertManagerAlarm)
          };
          this.hasAlarmResults = !!resp?.alarms.length;
          this.loadingAlarmResults = false;
        }
      })
    );
  }

  // fetches trace results for the given test and selected date range
  // this hydrates the path panel
  @action
  fetchTraceResults(options) {
    const { $syn } = this.store;
    const { requestId, test } = this;
    const { id, isPreset } = test;
    const { allowPath } = this.displayOptions;
    const { target_ips = [] } = options;
    const agent_ids = (options.agent_ids || [])
      .map((agentId) => `${agentId}`)
      .filter((agentId) => !!this.store.$syn.agents.get(agentId)); // filter out unknown/removed agents

    if (!allowPath) {
      return;
    }

    // Track the full set of fetched agent IDs and target IPs
    const mergedAgentIds = [...new Set([...(this.lastFetchedTraceParams.agentIds || []), ...agent_ids])];
    const mergedTargetIps = target_ips.length ? target_ips : this.lastFetchedTraceParams.targetIps;
    this.lastFetchedTraceParams = {
      ...this.lastFetchedTraceParams,
      agentIds: mergedAgentIds,
      targetIps: mergedTargetIps
    };

    this.loadingTraceResults = true;

    $syn.requests
      .fetchTestTraceroutee(id, { ...this.traceQueryTimeRange, id, agent_ids, target_ips }, { preset: isPreset })
      .then(
        action((traceResults) => {
          // check if request is relevant before storing response
          if (requestId === this.requestId) {
            // Re-read current cache at merge time to avoid stale closure.
            const existingTraceResults = this.traceResultsTs[this.resultTime];
            // Always merge new trace data with any existing cached data
            if (existingTraceResults) {
              const merged = { ...traceResults };
              const existingTimeSeries = existingTraceResults.trace_time_series || {};
              const newTimeSeries = traceResults.trace_time_series || {};
              const mergedTimeSeries = { ...existingTimeSeries };
              for (const ts of Object.keys(newTimeSeries)) {
                if (!mergedTimeSeries[ts]) {
                  mergedTimeSeries[ts] = newTimeSeries[ts];
                } else {
                  const existingTasks = mergedTimeSeries[ts].tasks || {};
                  const newTasks = newTimeSeries[ts].tasks || {};
                  const mergedTasks = { ...existingTasks };
                  for (const taskId of Object.keys(newTasks)) {
                    if (!mergedTasks[taskId]) {
                      mergedTasks[taskId] = newTasks[taskId];
                    } else {
                      mergedTasks[taskId] = {
                        ...mergedTasks[taskId],
                        agents: { ...mergedTasks[taskId].agents, ...newTasks[taskId].agents }
                      };
                    }
                  }
                  mergedTimeSeries[ts] = { ...mergedTimeSeries[ts], tasks: mergedTasks };
                }
              }
              merged.trace_time_series = mergedTimeSeries;
              merged.all_targets = uniqBy(
                (existingTraceResults.all_targets || []).concat(traceResults.all_targets || []),
                'target_ip'
              );
              merged.selected_targets = uniqBy(
                (existingTraceResults.selected_targets || []).concat(traceResults.selected_targets || []),
                'target_ip'
              );
              this.traceResultsTs[this.resultTime] = merged;
            } else {
              this.traceResultsTs[this.resultTime] = traceResults;
            }

            // capture the selected target ips from the last fetch (we'll send this back even with no results)
            // as we fetch along the timeline, it's possible to accumulate more targets, particularly with http/page load/hostname tests
            // this will ensure we keep them as selections in the target filter dropdowns
            this.lastFetchedTraceParams.allTargets = uniqBy(
              this.lastFetchedTraceParams.allTargets.concat(traceResults.all_targets || []),
              'target_ip'
            ).filter(({ target_ip }) => isIpValid(target_ip));

            this.lastFetchedTraceParams.selectedTargets = uniqBy(
              this.lastFetchedTraceParams.selectedTargets.concat(traceResults.selected_targets || []),
              'target_ip'
            ).filter(
              ({ target_ip }) =>
                // if we target ips here, it means we've selected a custom filter via dropdown so filter for those items to represent selected targets
                isIpValid(target_ip) && (!target_ips.length || target_ips.includes(target_ip))
            );

            this.loadingTraceResults = false;
          }
        })
      )
      .catch(
        // reset state on error so we at least remove loading state and can display empty results
        action(() => {
          this.lastFetchedTraceParams = this.defaults.lastFetchedTraceParams;
          this.loadingTraceResults = this.defaults.loadingTraceResults;
        })
      );
  }

  // allow loading of parent test results without the need to run through the entire fetch cycle
  @action
  loadResults({ start, end, timestamps, results, aggregation }) {
    // loading results manually has an impact on determining loading state
    // our 'init' flag is buried too deep for us to want to muck with it here for this purpose
    // we instead elect to add this specific flag here and check for it with the other loading flags
    this.initializedWithResults = true;

    // set the timestamp range
    this.timestamps = timestamps;

    if (start && end) {
      // fetch alarms
      this.fetchAlarmResults({
        start: moment.unix(start).toISOString(),
        end: moment.unix(end).toISOString()
      });
    }

    results.forEach((result, idx) => {
      const tests_health = result?.tests_health || null;
      const ts = Object.keys(tests_health?.health_ts || {})[0];
      const isLastResult = idx === results.length - 1;

      if (ts) {
        // populate the timeslice with results
        this.resultsTs[ts] = tests_health;

        if (isLastResult) {
          // the last result will set the current stamp time and aggregation levels
          this.resultTime = +ts;
          this.aggregationData = tests_health?.book?.aggregationData || {};

          // ensures subsequent requests on the alarm timeline use the same aggregation level we loaded with
          this.aggregationData.aggregation = aggregation;
        }
      }
    });
  }

  // ----- computed properties --------------------------------------------

  // primarily the data provider for the test results map component
  // returns a map keyed by agent id with overall health status and targets
  @computed
  get agentHealthTs() {
    const { test, results, hasResults } = this;

    if (hasResults) {
      return processTestResults(test, results);
    }

    return null;
  }

  // returns a map keyed by agent id containing target lists
  // used as a help for a variety of things such as the map component and health serializer
  @computed
  get agentTaskConfig() {
    const { results, resultsKeys, hasResults } = this;
    const config = {};

    if (hasResults) {
      const { agents } = results;
      const { agentKeys } = resultsKeys;

      for (let i = 0; i < agentKeys.length; i++) {
        const agentKey = agentKeys[i];
        const agent = agents[agentKeys[i]];

        if (agent.targets || agent.hostnames) {
          config[agentKey] = {};
        }

        if (agent.targets) {
          config[agentKey].targets = agent.targets;
        }

        if (agent.hostnames) {
          config[agentKey] = { ...config[agentKey], ...agent.hostnames };
        }
      }
    }

    return config;
  }

  // returns the aggregation value in number of seconds
  @computed
  get aggregation() {
    return this.aggregationData?.aggregation || 0;
  }

  // shortcut to check for aggregation status from test results
  @computed
  get isAggregated() {
    return !!this.aggregationData?.isAggregated;
  }

  // helper that returns a humanized string indicating aggregation value
  @computed
  get aggregationLabel() {
    return getAggregationLabel(this.aggregation);
  }

  /*
    Returns a map with lists of agent ids and target ips in desc order of alarm occurrences.
    This is used to give preference to the items with the highest number of associated alarms.
  */
  @computed
  get alarmedAgentIdsAndTargetIps() {
    const alarmedData = (this.alarmResults?.alarms || []).reduce(
      (acc, alarm) => {
        // a ping alarm is one that is alarmed on either the latency or packet loss metric
        const { agentId, metric } = alarm?.key?.value || {};
        const hasPingAlarm = ['latency', 'packet_loss'].includes(metric);

        if (agentId && hasPingAlarm) {
          // log an instance of the agent id associated with the ping alarm
          acc.agentIds[+agentId] = (acc.agentIds[+agentId] || 0) + 1;

          const { target } = alarm?.details || {};

          if (target && isIpValid(target)) {
            // if there's a valid ip target, on the ping alarm, log an instance of that too
            acc.targetIps[alarm.details.target] = (acc.targetIps[alarm.details.target] || 0) + 1;
          }
        }

        return acc;
      },
      {
        agentIds: {},
        targetIps: {}
      }
    );

    /*
        our map now looks like this:

        {
          agentIds: {
            123: 2,
            456: 1
          },
          targetIps: {
            '1.1.1.1': 3,
            '2.2.2.2': 1
          }
        }
      */

    return Object.keys(alarmedData).reduce(
      (acc, key) => ({
        ...acc,
        // each entry (agentIds and targetIps) is sorted in desc order
        // agent ids are kept consistently as numbers throughout the system
        [key]: Object.entries(alarmedData[key])
          .sort((a, b) => b[1] - a[1])
          .map(([value]) => (key === 'agentIds' ? +value : value))
      }),
      {}
    );
  }

  // currently used to sleep for an additional amount of time during export of larger tests
  // over time we should deprecate that strategy in favor of using isExportReady (see https://github.com/kentik/ui-app/pull/26203)
  @computed
  get bigDataSet() {
    const { test } = this;
    const agents = test.get('config.agents') || [];
    const testType = test.get('test_type');
    const bigTestTypes = [TEST_TYPES.APPLICATION_MESH, TEST_TYPES.NETWORK_GRID];

    return agents.length > 0 && bigTestTypes.includes(testType);
  }

  // used in the alarm timeline tooltip to determine whether we can offer a button to jump to raw data
  @computed
  get canShowRawHealth() {
    return !!this.maxRawLookback && this.isAggregated;
  }

  @computed
  get exportUrl() {
    // assemble an export-compatible url complete with hash settings
    let path = `${this.history.location.pathname.replace('/v4/', '/v4/export/')}${this.history.location.search}`;

    if (this.resultTime) {
      // tack on the currently selected timestamp if we have it
      path += `&exportTime=${this.resultTime}`;
    }

    return path;
  }

  // hydrates the alarms timeline with an ordered list of alarms
  @computed
  get healthTimeline() {
    const { alarmResults, timestamps } = this;

    if (timestamps.length > 0) {
      return getHealthTimeline({
        alarms: alarmResults?.alarms || [],
        timestamps
      });
    }

    return [];
  }

  @computed
  get loading() {
    const { init, initializedWithResults, loadingResults, loadingTimestamps, loadingAlarmResults } = this;
    return !(initializedWithResults || init) || loadingResults || loadingTimestamps || loadingAlarmResults;
  }

  // shortcut to the maximum lookback value that will return raw test results
  @computed
  get maxRawLookback() {
    return this.aggregationData?.maxRawLookback || 0;
  }

  // returns a date range ending at the currently selected timestamp, extending back using the max lookback for raw results.
  // if a date range is returned, it can be sent as the argument to onDateRangeChange
  @computed
  get maxRawLookbackDateRange() {
    if (this.canShowRawHealth) {
      return getRawHealthTimeRange({ endDate: this.resultTime, maxRawLookback: this.maxRawLookback });
    }

    return null;
  }

  // data provider for mesh ui component
  @computed
  get mesh() {
    const { results, hasResults } = this;

    if (hasResults) {
      return getMesh({ results });
    }

    return undefined;
  }

  // the currently selected timestamp converted to milliseconds
  @computed
  get resultTimeMs() {
    const { resultTime } = this;

    return resultTime ? resultTime * 1000 : null;
  }

  // returns the test results at the currently selected timestamp
  @computed
  get results() {
    const { resultTime, resultsTs, loading } = this;

    if (loading) {
      return null;
    }

    return resultsTs[resultTime] || null;
  }

  // determines whether the currently selected test results are valid
  @computed
  get hasResults() {
    const { results } = this;

    if (results) {
      return this.checkForTestResults(results);
    }

    return false;
  }

  // returns the trace results at the currently selected timestamp
  @computed
  get traceResults() {
    const { resultTime, traceResultsTs, loadingTraceResults } = this;

    if (loadingTraceResults) {
      return {};
    }

    return traceResultsTs[resultTime] || {};
  }

  // determines whether the currently selected test results are valid
  @computed
  get hasTraceResults() {
    const { traceResults } = this;

    if (traceResults) {
      return this.checkForTraceResults(traceResults);
    }

    return false;
  }

  // helper that produces a map of key listings test results
  @computed
  get resultsKeys() {
    const { results } = this;

    return {
      agentKeys: Object.keys(results?.agents || {}),
      taskKeys: Object.keys(results?.tasks || {}),
      healthTsKeys: Object.keys(results?.health_ts || {}),
      healthAggKeys: Object.keys(results?.health_agg || {})
    };
  }

  // used in the test results header to show configured target(s)
  @computed
  get targetValue() {
    const { test } = this;
    const testType = test.get('test_type');
    let targetValue = test.get('config.target.value');

    if (testType === TEST_TYPES.IP_ADDRESS && targetValue.split(',').length > 1) {
      targetValue = 'IP Grid';
    }
    if (testType === TEST_TYPES.HOSTNAME && targetValue.split(',').length > 3) {
      targetValue = `${targetValue.split(',').slice(0, 3).join()},...`;
    }

    return targetValue;
  }

  // buckets alarms keyed by timestamp
  @computed
  get timelineAlarmsByResultTimeMs() {
    return getTimelineAlarmsByResultTimeMs(this.healthTimeline);
  }

  // returns the list of alarms by the currently selected timestamp
  // used in the incident log panel in test results to hydrate the table based on selected alarm timeline slice
  @computed
  get selectedTimelineAlarms() {
    return this.timelineAlarmsByResultTimeMs[this.resultTimeMs] || [];
  }

  @computed
  get traceQueryOffset() {
    const { test } = this;
    return Math.max(test.frequency, 60);
  }

  // provides key properties used throughout all the timeline dataviews such as alarm and subtest time series charts
  @computed
  get timelineBounds() {
    const { timestamps } = this;
    if (timestamps?.length) {
      const { xMin, xMax, xAxisMin, xAxisMax, delta } = getTimelinebounds(timestamps);

      return { xMin, xMax, xAxisMin, xAxisMax, delta };
    }

    return this.traceTimelineBounds;
  }

  // same as above but related to trace results
  @computed
  get traceTimelineBounds() {
    const { hasTraceResults, traceResults } = this;

    if (hasTraceResults) {
      const { xMin, xMax, xAxisMin, xAxisMax, delta } = getTimelinebounds(traceResults?.trace_time_series);

      return { xMin, xMax, xAxisMin, xAxisMax, delta };
    }

    return {};
  }

  // data provider to the path panel
  @computed
  get traceroutes() {
    const { hasTraceResults, traceResults } = this;
    const { agentIds, selectedTargets } = this.lastFetchedTraceParams;
    const selectedIps = selectedTargets?.map(({ target_ip }) => target_ip) || [];

    if (hasTraceResults) {
      const result = getTraceroutes({ traceResults, selectedAgentIds: agentIds, selectedIps });
      return result;
    }

    return [];
  }

  @computed
  get traceQueryTimeRange() {
    const { resultTime } = this;

    return {
      start_time: resultTime,
      end_time: resultTime + this.traceQueryOffset
    };
  }

  // ----- class utils ------------------------------------------------

  // helper util that checks for valid test results
  // we break this out here because it gets used in a few places
  // we use it in the parent test results state (here) in the hasResults getter
  // but we also use it in subtest results (AgentResultsState)
  // subtest results also have a concept of trend test results and we need this same util for that
  checkForTestResults(results = {}) {
    const { health_ts = {}, tasks = {} } = results;
    const tsKeys = Object.keys(health_ts);
    const taskKeys = Object.keys(tasks);
    return tsKeys.length > 0 && taskKeys.length > 0;
  }

  // similar to the helper for test results above
  // there are logic differences related to this in parent and subtest results
  // so we break this out here and call as needed
  checkForTraceResults(traceResults = {}) {
    const { trace_time_series = {} } = traceResults;
    const tsKeys = Object.keys(trace_time_series);
    return !!tsKeys.length;
  }

  // helper used during date processing
  checkForDateRange({ startDate, endDate, lookbackSeconds }) {
    if (startDate !== undefined && endDate !== undefined && lookbackSeconds !== undefined) {
      return { startDate, endDate, lookbackSeconds };
    }

    return null;
  }

  // rewrites a set of date properties using logic that determines minimum lookbacks for the given test/frequency
  enforceMinLookback({ lookbackSeconds, startDate, endDate }) {
    const { test } = this;

    return {
      lookbackSeconds,
      startDate,
      endDate,
      ...(getMinLookback(test, lookbackSeconds) || {})
    };
  }

  // ----- change handlers ------------------------------------------------

  // selects a timestamp and loads test results (fresh or from cache)
  @action
  onChangeResultTimeMs(resultTimeMs) {
    this.resultTime = resultTimeMs / 1000;
    const { agentIds: agent_ids, targetIps: target_ips } = this.lastFetchedTraceParams;

    this.fetchResultsByTs(this.resultTime, this.aggregation);
    this.fetchTraceResults({
      agent_ids,
      target_ips
    });
  }

  // processes a date selection change resulting in a fresh fetch of data
  onDateRangeChange({ lookbackSeconds, startDate, endDate }) {
    const { displayOptions, history } = this;
    const { $auth } = this.store;
    const dateObj = this.enforceMinLookback({ lookbackSeconds, startDate, endDate });
    const v1 = !!history?.location.pathname.includes('/v1');

    if (displayOptions.usePageComponent && !$auth.hasSudo) {
      if (v1) {
        if (!dateObj.startDate || dateObj.startDate > SYNGEST_CUTOVER) {
          history.push(`${history.location.pathname.replace('/v1', '')}`, dateObj);
        }
      } else if (dateObj.startDate && dateObj.startDate < SYNGEST_CUTOVER) {
        history.push(`${history.location.pathname}/v1`, dateObj);
      }
    }

    // don't use lookbackSeconds, does not align well across services
    if (lookbackSeconds) {
      const { start, end } = getStartAndEndFromLookback(lookbackSeconds, 120);
      dateObj.actualStartDate = start.unix();
      dateObj.actualEndDate = end.unix();
    } else {
      dateObj.actualStartDate = dateObj.startDate;
      dateObj.actualEndDate = dateObj.endDate;
    }

    this.fetch({ startDate: dateObj.actualStartDate, endDate: dateObj.actualEndDate });
    this.setDateRange(dateObj);

    if (displayOptions.allowSettings) {
      this.store.$exports.setHash(
        {
          hashedLookbackSeconds: dateObj.lookbackSeconds,
          hashedStartDate: dateObj.startDate,
          hashedEndDate: dateObj.endDate
        },
        true,
        true
      ); // merge and replace the querystring to just be the q hash
    }
  }

  // multi-property setter used in the date selection change handler
  @action
  setDateRange({ lookbackSeconds, startDate, endDate, minLookbackSeconds, actualStartDate, actualEndDate }) {
    // values could potentially come in via url params so make sure to coerce
    this.lookbackSeconds = +lookbackSeconds;
    this.startDate = +startDate;
    this.endDate = +endDate;
    this.actualStartDate = +actualStartDate;
    this.actualEndDate = +actualEndDate;

    if (minLookbackSeconds !== undefined) {
      this.minLookbackSeconds = minLookbackSeconds;
    }
  }

  // sets the selected tab id in parent test results
  @action
  onPageTabChange = (id) => {
    const { allowSettings } = this.displayOptions;
    const { $exports } = this.store;
    this.pageTabId = id;
    if (allowSettings) {
      $exports.setHash({ hashedPageTabId: id }, true, true); // merge and replace the querystring to just be the q hash
    }
  };

  // in the case of an export, we'll receive the timestamp to be selected via the 'exportTime' url param
  // set it here but fall back to the default value if not available
  updateExportTime(history) {
    if (this.store.$app.isExport) {
      this.exportTime = parseQueryString(history?.location?.search).exportTime || this.defaults.exportTime;
    }
  }
}

export default TestResultsState;
